import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  IndianRupee, Users, Target, TrendingUp, Award, FileText, ChevronDown, ChevronUp,
  ArrowLeft, Loader2, DollarSign, Percent, LayoutDashboard, Settings, FileSearch, Zap
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from 'recharts';
import { toast } from 'sonner';

const fmt = (v) => {
  if (v == null || isNaN(v)) return '-';
  return Number(v).toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const COLORS = ['#6d28d9', '#2563eb', '#059669', '#d97706', '#dc2626', '#7c3aed', '#0891b2'];
const FONT = { heading: "'Inter', system-ui, sans-serif", body: "'Inter', system-ui, sans-serif" };

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-md p-2.5 shadow-xl text-xs">
      <p className="text-slate-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-medium">
          {p.name}: {typeof p.value === 'number' ? fmt(p.value) : p.value}
        </p>
      ))}
    </div>
  );
};

export default function IncentivePayoutDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedId, setExpandedId] = useState(null);
  const [rawFieldsModal, setRawFieldsModal] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get('/incentive-dashboard/overview')
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load incentive dashboard'))
      .finally(() => setLoading(false));
  }, []);

  const NAV = [
    { id: 'overview', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'submissions', label: `Submissions${data ? ` (${data.summary?.total_submissions || 0})` : ''}`, icon: FileText },
  ];

  if (loading) {
    return (
      <div className="flex h-screen bg-white">
        <aside className="w-56 bg-slate-900 flex-shrink-0" />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
        </div>
      </div>
    );
  }

  if (!data || data.error) {
    return (
      <div className="flex h-screen bg-white">
        <aside className="w-56 bg-slate-900 flex flex-col flex-shrink-0">
          <div className="p-5"><p className="text-white text-sm font-bold">Incentive Payout</p></div>
          <div className="mt-auto p-3 border-t border-slate-800">
            <button onClick={() => navigate('/dashboard')} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-sm text-xs text-slate-500 hover:bg-slate-800 hover:text-white transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" />Back to Command Center
            </button>
          </div>
        </aside>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">{data?.error || 'No incentive form found'}</p>
          </div>
        </div>
      </div>
    );
  }

  const { summary: s, submissions, by_employee, by_month } = data;

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden" style={{ fontFamily: FONT.body }} data-testid="incentive-dashboard">
      {/* Sidebar */}
      <aside className="w-56 bg-slate-900 flex flex-col flex-shrink-0">
        <div className="p-5 border-b border-slate-800">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-7 h-7 rounded-sm bg-violet-500/20 flex items-center justify-center">
              <Award className="w-4 h-4 text-violet-300" />
            </div>
            <p className="text-white text-sm font-bold" style={{ fontFamily: FONT.heading }}>Incentive Payout</p>
          </div>
          <p className="text-slate-500 text-[10px] pl-9 leading-tight">{data.form?.name || 'Sales Incentive'}</p>
        </div>
        <nav className="flex-1 p-3 space-y-1 mt-2">
          {NAV.map(item => (
            <button
              key={item.id}
              data-testid={`nav-${item.id}`}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-sm text-xs font-medium transition-all duration-150 ${
                activeTab === item.id
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <item.icon className="w-3.5 h-3.5 flex-shrink-0" />{item.label}
            </button>
          ))}
        </nav>
        {/* Sidebar stats */}
        <div className="px-4 pb-3 space-y-2">
          <div className="bg-slate-800/50 rounded-sm p-2.5">
            <p className="text-[9px] text-slate-500 uppercase">Total Incentive</p>
            <p className="text-sm font-bold text-violet-400">{fmt(s.total_incentive)}</p>
          </div>
          <div className="bg-slate-800/50 rounded-sm p-2.5">
            <p className="text-[9px] text-slate-500 uppercase">Avg Achievement</p>
            <p className={`text-sm font-bold ${s.avg_achievement_pct >= 80 ? 'text-emerald-400' : s.avg_achievement_pct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>{s.avg_achievement_pct}%</p>
          </div>
        </div>
        <div className="p-3 border-t border-slate-800">
          <button
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-command-center"
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-sm text-xs text-slate-500 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />Back to Command Center
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-14 bg-white border-b border-slate-200 flex items-center px-6 gap-4 flex-shrink-0">
          <h1 className="text-sm font-bold text-slate-900 flex-1" style={{ fontFamily: FONT.heading }}>
            {data.form?.name || 'Incentive Payout Dashboard'}
          </h1>
          <span className="text-[10px] text-slate-400">
            {s.total_submissions} submission{s.total_submissions !== 1 ? 's' : ''} &middot; {s.unique_employees} employee{s.unique_employees !== 1 ? 's' : ''}
          </span>
        </header>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {activeTab === 'overview' && <OverviewTab s={s} by_employee={by_employee} by_month={by_month} />}
          {activeTab === 'submissions' && (
            <SubmissionsTab
              submissions={submissions}
              expandedId={expandedId}
              setExpandedId={setExpandedId}
              onViewRaw={(sub) => setRawFieldsModal(sub)}
            />
          )}
        </div>
      </div>

      {/* Raw Fields Modal */}
      <RawFieldsDialog submission={rawFieldsModal} onClose={() => setRawFieldsModal(null)} />
    </div>
  );
}

function OverviewTab({ s, by_employee, by_month }) {
  return (
    <>
      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard icon={Target} label="Total Target" value={fmt(s.total_target)} color="text-slate-700" accent="bg-slate-100" />
        <KpiCard icon={TrendingUp} label="Total Achieved" value={fmt(s.total_achieved)} color="text-emerald-600" accent="bg-emerald-50" />
        <KpiCard icon={Percent} label="Achievement" value={`${s.avg_achievement_pct}%`} color={s.avg_achievement_pct >= 80 ? 'text-emerald-600' : s.avg_achievement_pct >= 50 ? 'text-amber-600' : 'text-red-500'} accent="bg-violet-50" />
        <KpiCard icon={Award} label="Total Incentive" value={fmt(s.total_incentive)} color="text-violet-600" accent="bg-violet-50" />
        <KpiCard icon={DollarSign} label="Commission" value={fmt(s.total_commission)} color="text-blue-600" accent="bg-blue-50" />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="Payment Expected" value={fmt(s.total_payment_expected)} />
        <StatCard label="Payment Received" value={fmt(s.total_payment_received)} warn={s.total_payment_received < s.total_payment_expected} />
        <StatCard label="Write-off" value={fmt(s.total_writeoff)} warn={s.total_writeoff > 0} />
        <StatCard label="Unique Employees" value={s.unique_employees} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {by_employee.length > 0 && (
          <Card className="border p-5" data-testid="employee-chart">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-4">Incentive by Employee</p>
            <ResponsiveContainer width="100%" height={Math.max(200, by_employee.slice(0, 8).length * 40)}>
              <BarChart data={by_employee.slice(0, 8)} layout="vertical" margin={{ left: 5, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11, fill: '#334155' }} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="total_incentive" name="Incentive" radius={[0, 4, 4, 0]} barSize={24}>
                  {by_employee.slice(0, 8).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        {by_month.length > 0 && (
          <Card className="border p-5" data-testid="month-chart">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-4">Monthly Breakdown</p>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={by_month} margin={{ left: 5, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                <YAxis tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="target" name="Target" fill="#cbd5e1" radius={[3, 3, 0, 0]} />
                <Bar dataKey="achieved" name="Achieved" fill="#6d28d9" radius={[3, 3, 0, 0]} />
                <Bar dataKey="incentive" name="Incentive" fill="#059669" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>
    </>
  );
}

function SubmissionsTab({ submissions, expandedId, setExpandedId, onViewRaw }) {
  if (submissions.length === 0) {
    return <div className="text-center py-20 text-sm text-slate-400">No submissions yet</div>;
  }

  return (
    <Card className="border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50/80">
            <TableHead className="text-[10px] uppercase w-8" />
            <TableHead className="text-[10px] uppercase">Employee</TableHead>
            <TableHead className="text-[10px] uppercase">Month</TableHead>
            <TableHead className="text-[10px] uppercase text-right">Target</TableHead>
            <TableHead className="text-[10px] uppercase text-right">Achieved</TableHead>
            <TableHead className="text-[10px] uppercase text-right">Achievement</TableHead>
            <TableHead className="text-[10px] uppercase text-right">Incentive</TableHead>
            <TableHead className="text-[10px] uppercase text-right">Commission</TableHead>
            <TableHead className="text-[10px] uppercase">Status</TableHead>
            <TableHead className="text-[10px] uppercase w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {submissions.map(r => (
            <SubmissionRow
              key={r.id}
              row={r}
              expanded={expandedId === r.id}
              onToggle={() => setExpandedId(expandedId === r.id ? null : r.id)}
              onViewRaw={() => onViewRaw(r)}
            />
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function SubmissionRow({ row: r, expanded, onToggle, onViewRaw }) {
  return (
    <>
      <TableRow className="cursor-pointer hover:bg-violet-50/30" onClick={onToggle} data-testid={`incentive-row-${r.id}`}>
        <TableCell>{expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}</TableCell>
        <TableCell>
          <div className="text-sm font-medium text-slate-800">{r.employee_name || '-'}</div>
          <div className="text-[10px] text-slate-400">{r.emp_code ? `#${r.emp_code}` : ''} {r.emp_email || ''}</div>
        </TableCell>
        <TableCell className="text-xs text-slate-600">{r.reporting_month || '-'}</TableCell>
        <TableCell className="text-xs text-right">{fmt(r.target)}</TableCell>
        <TableCell className="text-xs text-right font-medium">{fmt(r.achieved_revenue)}</TableCell>
        <TableCell className="text-xs text-right">
          <span className={`font-semibold ${r.achievement_pct >= 80 ? 'text-emerald-600' : r.achievement_pct >= 50 ? 'text-amber-600' : 'text-red-500'}`}>
            {r.achievement_pct}%
          </span>
        </TableCell>
        <TableCell className="text-xs text-right font-semibold text-violet-600">{fmt(r.incentive)}</TableCell>
        <TableCell className="text-xs text-right text-blue-600">{fmt(r.commission)}</TableCell>
        <TableCell>
          <Badge variant="outline" className={`text-[9px] ${
            r.status === 'approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
            r.status === 'rejected' ? 'bg-red-50 text-red-600 border-red-200' :
            r.status === 'pending' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-50 text-slate-500'
          }`}>{r.status}</Badge>
        </TableCell>
        <TableCell onClick={e => e.stopPropagation()}>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="View all raw fields" onClick={onViewRaw} data-testid={`view-raw-${r.id}`}>
            <FileSearch className="w-3.5 h-3.5 text-slate-400" />
          </Button>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={10} className="bg-slate-50/50 p-4">
            <SubmissionDetail row={r} onViewRaw={onViewRaw} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function SubmissionDetail({ row: r, onViewRaw }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 text-xs">
        <div><span className="text-slate-400">Employee</span><div className="font-medium">{r.employee_name}</div></div>
        <div><span className="text-slate-400">Emp Code</span><div className="font-mono">{r.emp_code || '-'}</div></div>
        <div><span className="text-slate-400">Email</span><div>{r.emp_email || '-'}</div></div>
        <div><span className="text-slate-400">Team Lead</span><div>{r.team_lead_email || '-'}</div></div>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        <DetailCard label="Target" value={fmt(r.target)} />
        <DetailCard label="Achieved Revenue" value={fmt(r.achieved_revenue)} color="text-emerald-600" />
        <DetailCard label="Expected Revenue" value={fmt(r.expected_revenue)} />
        <DetailCard label="Achievement %" value={`${r.achievement_pct}%`} color={r.achievement_pct >= 80 ? 'text-emerald-600' : 'text-amber-600'} />
        <DetailCard label="Payment Expected" value={fmt(r.payment_expected)} />
        <DetailCard label="Payment Received" value={fmt(r.payment_received)} color={r.payment_received >= r.payment_expected ? 'text-emerald-600' : 'text-red-500'} />
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        <DetailCard label="Commission" value={fmt(r.commission)} color="text-blue-600" />
        <DetailCard label="Incentive" value={fmt(r.incentive)} color="text-violet-600" />
        <DetailCard label="Approved Incentive" value={fmt(r.approved_incentive)} color="text-violet-700" />
        <DetailCard label="Write-off" value={fmt(r.writeoff)} color={r.writeoff > 0 ? 'text-red-500' : 'text-slate-400'} />
        <DetailCard label="Adjustment" value={fmt(r.adjustment)} />
        <DetailCard label="Hot Leads" value={r.hot_leads} />
      </div>

      <div className="flex items-center justify-between pt-2 border-t">
        <div className="flex items-center gap-4 text-[10px] text-slate-400">
          <span>Payment: <span className="text-slate-600 font-medium">{r.payment_status || '-'}</span></span>
          <span>Approved by: <span className="text-slate-600 font-medium">{(r.approved_by || []).join(', ') || '-'}</span></span>
          {r.adjustment_details && <span>Note: <span className="text-slate-600">{r.adjustment_details}</span></span>}
          <span>Submitted: {r.created_at ? new Date(r.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</span>
        </div>
        <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" onClick={onViewRaw} data-testid={`view-raw-detail-${r.id}`}>
          <FileSearch className="w-3 h-3" /> View All Raw Fields
        </Button>
      </div>
    </div>
  );
}

function RawFieldsDialog({ submission, onClose }) {
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!submission) { setRawData(null); return; }
    setLoading(true);
    api.get(`/incentive-dashboard/raw-fields/${submission.id}`)
      .then(r => setRawData(r.data))
      .catch(() => {
        setRawData({ fields: Object.entries(submission).filter(([k]) => !['id','status','created_at','approved_by'].includes(k)).map(([k,v]) => ({ field: k, value: v })) });
      })
      .finally(() => setLoading(false));
  }, [submission]);

  return (
    <Dialog open={!!submission} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh]" data-testid="raw-fields-modal">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <FileSearch className="w-4 h-4 text-violet-500" />
            All Form Fields — {submission?.employee_name || 'Submission'}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : rawData ? (
          <ScrollArea className="max-h-[60vh]">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-[10px] uppercase font-semibold w-[45%]">Field Name (from Zoho)</TableHead>
                  <TableHead className="text-[10px] uppercase font-semibold">Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(rawData.fields || []).map((f, i) => (
                  <TableRow key={i} className={i % 2 === 0 ? '' : 'bg-slate-50/50'}>
                    <TableCell className="text-xs font-medium text-slate-700">{f.field}</TableCell>
                    <TableCell className="text-xs text-slate-600 break-all">
                      {Array.isArray(f.value) ? f.value.join(', ') : String(f.value ?? '-')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function KpiCard({ icon: Icon, label, value, color, accent }) {
  return (
    <Card className="border p-4 flex items-center gap-3.5">
      <div className={`w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 ${accent}`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <div className="min-w-0">
        <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
        <p className={`text-lg font-bold truncate ${color}`}>{value}</p>
      </div>
    </Card>
  );
}

function StatCard({ label, value, warn }) {
  return (
    <Card className="border p-3 text-center">
      <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
      <p className={`text-base font-bold ${warn ? 'text-red-500' : 'text-slate-700'}`}>{value}</p>
    </Card>
  );
}

function DetailCard({ label, value, color = 'text-slate-700' }) {
  return (
    <div className="bg-white border rounded-sm p-2 text-center">
      <p className="text-[9px] text-slate-400 uppercase truncate">{label}</p>
      <p className={`text-sm font-semibold ${color}`}>{value}</p>
    </div>
  );
}
